import React, { useState } from "react";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import logoBlack from "../Assets/final-logo-black.png";
import firemenBackground from "../Assets/dashboardlogin.jpg";
import { useAuth } from "../contexts/AuthContext";

const LoginPage = styled.div`
  display: flex;
  min-height: 100vh;
`;

const ImageContainer = styled.div`
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url(${firemenBackground}) no-repeat center center;
  background-size: cover;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ImageText = styled.div`
  background-color: #eb2529;
  font-size: 3.2rem;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: 600;
  font-family: Arial, Helvetica, sans-serif;
  padding: 5px 15px;
`;

const LoginContainer = styled.div`
  width: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  flex-direction: column;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.4);
  background-color: white;
`;

const LoginLogo = styled.img`
  margin-bottom: 26px;
  width: 100%;
  max-width: 250px;
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 425px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const LoginInput = styled.input`
  margin-bottom: 15px;
  border: 1px solid #ccc;
  padding: 16px;
  border-radius: 4px;
  font-size: 1.1rem;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    outline: none;
  }
`;

const LoginRemember = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;

  input[type="checkbox"] {
    margin-right: 5px;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 10px;
  background-color: #007bff;
  font-weight: 900;
  color: white;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;

const LoginError = styled.span`
  color: #eb2529;
  margin-top: 10px;
  text-align: center;
  font-weight: 500;
`;

const Warning = styled.div`
  color: #eb2529;
  font-size: 0.85rem;
  font-weight: 800;
  margin-top: 20px;
  max-width: 425px;
`;

const WarningHeader = styled.span`
  width: 100%;
  text-align: center;
  display: block;
  margin-bottom: 6px;
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    try {
      setLoading(true);
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/");
    } catch (error: any) {
      console.error("Login error:", error.message);

      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/user-not-found"
      ) {
        setError("Username or password is incorrect");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many failed login attempts. Please try again later.");
      } else {
        setError("Failed to login. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginPage>
      <ImageContainer>
       
      </ImageContainer>

      <LoginContainer>
        <LoginLogo src={logoBlack} alt="Logo" />

        <LoginForm onSubmit={handleLogin}>
          <LoginInput
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <LoginInput
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <LoginRemember>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="rememberMe">Remember me</label>
          </LoginRemember>

          <LoginButton type="submit" disabled={loading}>
            {loading ? "Logging in..." : "LOGIN"}
          </LoginButton>

          {error && <LoginError>{error}</LoginError>}
        </LoginForm>

        <Warning>
          <WarningHeader>
          *** OFFICIAL USE ONLY – SYSTEM WARNING NOTICE ***
          </WarningHeader>
          <p>
    This system is the property of Ladder Suite LLC and is provided for official use by 
    authorized government personnel and affiliated agencies only. Unauthorized access, use, 
    or modification of this system is strictly prohibited and may result in disciplinary action, 
    civil penalties, and/or criminal prosecution under applicable laws.
  </p>
  <p>
    All activity on this system is subject to monitoring, recording, and auditing. By proceeding, 
    you expressly consent to such monitoring and acknowledge your obligation to comply with all 
    applicable policies, including Ladder Suite’s 
    Terms of Service and any 
    governing agency regulations.
  </p>
  <p>
    If you are not an authorized user or do not agree to these conditions, <strong>log off immediately</strong>.
  </p>
        </Warning>
      </LoginContainer>
    </LoginPage>
  );
};

export default Login;
